import React, { Component } from 'react';
import PropTypes from 'prop-types';
import message from 'antd/lib/message';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  accountDocumentTypes,
  accountDocumentTypesOptional,
  accountDocumentObligations,
  accountDocumentAllowedMimeTypes,
  accountRoles,
  contractType,
  contractStatus,
  debtorRegistrationActions,
  creditorRegistrationActions,
  loanActions,
} from '../../../common/enums';
import {
  selectToken,
  selectAccountId,
  selectAccountRegistrationAction,
  selectAccountRole,
} from '../../../features/auth/ducks';
import { selectIsInProgress } from '../../../features/spinner/ducks';
import { apiCallIds as accountManagementApiCallIds } from '../../../features/account-management/api';
import { apiCallIds as filesApiCallIds } from '../../../features/files/api';
import { removeUploadedFileAction, resetUploadedFilesAction } from '../../../features/files/ducks';
import {
  selectDocuments,
  selectContracts,
  selectDocumentsVersion,
  fetchAccountDocumentsActions,
  fetchAccountContractsActions,
  updateAccountDocumentsActions,
  submitAccountDocumentsActions,
  uploadFileAndUpdateAccountDocumentsActions,
  saveVerificationResultAction,
} from '../ducks';
import DocumentManagement from '../components/DocumentManagement';
import { selectSetting } from '../../../features/settings/ducks';
import { addLoanDocumentActions, removeLoanDocumentActions } from '../../../features/loans/ducks';
import { selectLoans, fetchLoansActions } from '../../../features/loans/ducks';

// NEW: PSD2 ducks (you'll need to add these to your store/sagas)
import {
  fetchPsd2InstitutionsActions,
  createPsd2RequisitionActions,
  setPsd2LastResult,
  fetchPsd2LoanImportedAccountsActions,
  selectPsd2Institutions,
  selectPsd2LastResult,
  selectPsd2LoanImportedAccounts,
} from '../../../features/psd2/ducks';

const createDocumentUploadType = documentType => `dashboardDocuments/${documentType}`;

const mapStateToProps = state => ({
  account: state.auth.account,
  accountId: selectAccountId(state),
  accountRole: selectAccountRole(state),
  documents: selectDocuments(state),
  contracts: selectContracts(state),
  documentsVersion: selectDocumentsVersion(state),
  isUpdating: selectIsInProgress(state, accountManagementApiCallIds.UPDATE_ACCOUNT_DOCUMENTS),
  isUploading: selectIsInProgress(state, filesApiCallIds.UPLOAD_FILE),
  isSavingVerificationResult: selectIsInProgress(state, accountManagementApiCallIds.SAVE_VERIFICATION_RESULT),
  registrationAction: selectAccountRegistrationAction(state),
  token: selectToken(state),
  useBiometrics: selectSetting(state, 'BIOMETRICS_ON'),
  usePSD2Integration: selectSetting(state, 'PSD2_INTEGRATION_ON'),
  PSD2Discount: selectSetting(state, 'PSD2_INTEREST_DISCOUNT'),
  loans: selectLoans(state),

  // NEW
  psd2Institutions: selectPsd2Institutions(state),
  psd2LastResult: selectPsd2LastResult(state),
  psd2ImportedAccounts: selectPsd2LoanImportedAccounts(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchAccountDocuments: fetchAccountDocumentsActions.request,
      fetchAccountContracts: fetchAccountContractsActions.request,
      removeUploadedFile: removeUploadedFileAction,
      resetUploadedFiles: resetUploadedFilesAction,
      submitAccountDocuments: submitAccountDocumentsActions.request,
      updateAccountDocuments: updateAccountDocumentsActions.request,
      uploadFileAndUpdateAccountDocuments: uploadFileAndUpdateAccountDocumentsActions,
      saveVerificationResult: saveVerificationResultAction.request,
      addLoanDocument: addLoanDocumentActions.request,
      removeLoanDocument: removeLoanDocumentActions.request,
      fetchLoans: fetchLoansActions.request,

      // NEW
      fetchPsd2Institutions: fetchPsd2InstitutionsActions.request,
      createPsd2Requisition: createPsd2RequisitionActions.request,
      setPsd2LastResult,
      fetchPsd2LoanImportedAccounts: fetchPsd2LoanImportedAccountsActions.request,
    },
    dispatch
  ),
});

const uploadDocumentsActions = [
  debtorRegistrationActions.UPLOAD_DOCUMENTS,
  creditorRegistrationActions.UPLOAD_DOCUMENTS,
  debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
  loanActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
];

@connect(mapStateToProps, mapDispatchToProps)
export default class DocumentsContainer extends Component {
  static propTypes = {
    accountId: PropTypes.string.isRequired,
    accountRole: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    documents: PropTypes.array.isRequired,
    contracts: PropTypes.array.isRequired,
    documentsVersion: PropTypes.number.isRequired,
    registrationAction: PropTypes.string,
    token: PropTypes.string.isRequired,
  };

  state = {
    documents: [
      {
        accountRoles: [accountRoles.CREDITOR, accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.ID_CARD_SCAN_FRONT],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.ID_CARD_SCAN_FRONT,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.ID_CARD_SCAN_FRONT),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_DOCUMENTS,
      },
      {
        accountRoles: [accountRoles.CREDITOR, accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.ID_CARD_SCAN_BACK],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.ID_CARD_SCAN_BACK,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.ID_CARD_SCAN_BACK),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_DOCUMENTS,
      },
      {
        accountRoles: [accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.BANK_ACCOUNT_STATEMENT_1],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.BANK_ACCOUNT_STATEMENT_1,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.BANK_ACCOUNT_STATEMENT_1),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
      },
      {
        accountRoles: [accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.BANK_ACCOUNT_STATEMENT_2],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.BANK_ACCOUNT_STATEMENT_2,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.BANK_ACCOUNT_STATEMENT_2),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
      },
      {
        accountRoles: [accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.PAYROLL_STATEMENT_1],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.PAYROLL_STATEMENT_1,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.PAYROLL_STATEMENT_1),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
      },
      {
        accountRoles: [accountRoles.DEBTOR],
        allowedDocumentTypes: [accountDocumentTypes.PAYROLL_STATEMENT_2],
        documentObligation: accountDocumentObligations.REQUIRED,
        documentType: accountDocumentTypes.PAYROLL_STATEMENT_2,
        documentUploadType: createDocumentUploadType(accountDocumentTypes.PAYROLL_STATEMENT_2),
        file: {},
        registrationAction: debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS,
      },
    ],
    optionalDocumentType: accountDocumentTypesOptional[0],
    documentsFetched: false,
  };

  componentWillReceiveProps(nextProps) {
    const { accountId } = this.props;
    let documents = nextProps.documents;
    const { loans } = nextProps;

    const loan = loans.find(loan => loan.accountId == accountId && loan.status == 'CNUD');

    if (loan && loan.documents) {
      documents = [...nextProps.documents, ...loan.documents.documents];
    }

    this.pairDocuments(documents);

    // Fetch imported accounts when loan becomes available
    if (parseInt(nextProps.usePSD2Integration) === 1 && loans !== this.props.loans && !this._fetchedImportedAccounts) {
      const targetLoan = loan || Array.from(loans).sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))[0];
      if (targetLoan && targetLoan.psd2Requisitions && targetLoan.psd2Requisitions.length) {
        this._fetchedImportedAccounts = true;
        nextProps.actions.fetchPsd2LoanImportedAccounts(targetLoan.id);
      }
    }
  }

  findCurrentLoan = () => {
    return Array.from(this.props.loans).sort((a,b)=> Date(a.updatedAt) - Date(b.updatedAt))[0]
  }


  componentDidMount() {
    const { actions, accountId, usePSD2Integration } = this.props;

    actions.fetchAccountDocuments(accountId);
    actions.fetchAccountContracts(accountId);
    actions.fetchLoans({ append: false, options: {} });

    // PSD2 institutions preload
    if (parseInt(usePSD2Integration) === 1) {
      actions.fetchPsd2Institutions();
    }

    const query = (window.location.search || window.location.hash.substring(window.location.hash.indexOf('?') +1)) || '';

    const ref = query.startsWith('ref') ? query.substring(query.indexOf('=') + 1) : '';

    const hasError = query.indexOf('&') !== -1 && query.split('&').some((str)=> str.startsWith('error'))

    const curLoan = this.findCurrentLoan()

    const successfullyFinishedPsd2 = curLoan ? ref.startsWith(curLoan.id) : false

    actions.setPsd2LastResult(hasError ?  'error' : successfullyFinishedPsd2 ? 'success' : 'idle' );

  }

  onOptionalDocumentChange = optionalDocumentType => {
    const { documents } = this.props;

    if (documents.map(doc => doc.type).indexOf(optionalDocumentType) !== -1) {
      this.pairDocuments(documents);
    } else {
      this.setState(prevState => ({
        ...prevState,
        optionalDocumentType,
        documents: prevState.documents.map(doc => {
          if (doc.documentObligation === accountDocumentObligations.OPTIONAL) {
            doc.documentType = optionalDocumentType;
            doc.documentUploadType = createDocumentUploadType(optionalDocumentType);
            doc.file = {};
          }
          return doc;
        }),
      }));
    }
  };

  onUpload = uploadingDocument => uploadData => {
    const { documentsVersion, accountId, actions, documents } = this.props;

    const formData = new FormData();
    formData.append('dataFile', uploadData.file);

    const documentType =
      uploadingDocument.documentObligation === accountDocumentObligations.OPTIONAL
        ? this.state.optionalDocumentType
        : uploadingDocument.documentType;
    const documentUploadType = createDocumentUploadType(documentType);

    const filteredDocuments = [...documents];

    const documentUploadTypes = [
      ...filteredDocuments.map(doc => createDocumentUploadType(doc.type)),
      documentUploadType,
    ];

    if (
      uploadingDocument.documentType !== accountDocumentTypes.ID_CARD_SCAN_FRONT &&
      uploadingDocument.documentType !== accountDocumentTypes.ID_CARD_SCAN_BACK
    ) {
      let loan = this.findCurrentLoanAwaitingStatements();

      actions.addLoanDocument({
        uploadFilePayload: {
          uploadedFilesId: documentUploadType,
          formData: formData,
          single: true,
        },
        loanId: loan ? loan.id : null,
        type: uploadingDocument.documentType,
      });

      return;
    } else {
      actions.uploadFileAndUpdateAccountDocuments({
        uploadFilePayload: {
          uploadedFilesId: documentUploadType,
          formData: formData,
          single: true,
        },
        fileType: documentType,
        updateAccountDocumentsPayload: {
          accountId: accountId,
          uploadedFilesId: documentUploadTypes,
          documentsData: {
            documents: filteredDocuments.map(document => ({
              file: {
                id: document.file.id,
                name: document.file.name,
              },
              type: document.type,
            })),
            version: documentsVersion,
          },
        },
      });
    }
  };

  onRemove = doc => file => {
    this.props.actions.removeUploadedFile({
      uploadedFilesId: doc.documentUploadType,
      fileId: file.uid,
    });
    return true;
  };

  onSubmit = () => {
    const { accountId, actions } = this.props;
    actions.submitAccountDocuments({
      accountId,
      redirect: '/dashboard',
    });
  };

  onSaveVerificationResult = async verificationResult => {
    const { accountId, actions } = this.props;

    await actions.saveVerificationResult({
      accountId,
      verificationResult: verificationResult,
      redirect: '/dashboard/loans',
    });
  };

  pairDocuments = documents => {
    const { accountRole } = this.props;

    const uploadingAvailable = this.uploadingAvailable();
    documents = documents.sort(
      (a, b) => new Date(b.file.updatedAt).getTime() - new Date(a.file.updatedAt).getTime()
    );

    if (uploadingAvailable) {
      documents = documents.filter(
        doc => new Date(doc.file.updatedAt).getTime() >= new Date(this.props.account.updatedAt).getTime()
      );
    }

    let optionalDocumentType = accountDocumentTypesOptional[0];

    const filteredDocuments = this.state.documents.filter(
      doc => doc.accountRoles.indexOf(accountRole) !== -1
    );

    const newStateDocuments = filteredDocuments.map(stateDocument => {
      const pairedDoc = documents.find(
        doc => stateDocument.allowedDocumentTypes.indexOf(doc.type) !== -1
      );

      if (pairedDoc) {
        stateDocument.file = { ...pairedDoc.file };
        stateDocument.documentType = pairedDoc.type;
        stateDocument.documentUploadType = createDocumentUploadType(pairedDoc.type);
        if (stateDocument.documentObligation === accountDocumentObligations.OPTIONAL) {
          optionalDocumentType = pairedDoc.type;
        }
      }
      return stateDocument;
    });

    this.setState(prevState => ({
      documents: newStateDocuments,
      optionalDocumentType,
      documentsFetched: true,
    }));
  };

  findCurrentLoanAwaitingStatements = () => {
    const loan = this.props.loans.find(
      loan => loan.accountId == this.props.accountId && loan.status == 'CNUD'
    );
    return loan;
  };

  isDocFilled = (doc,usePSD2Integration,hasPsd2Requisitions,psd2LastResult) => {
    return doc.file.id || (
      usePSD2Integration > 0
       && (hasPsd2Requisitions || psd2LastResult === 'success') && (        
        doc.documentType === accountDocumentTypes.BANK_ACCOUNT_STATEMENT_1
         || doc.documentType === accountDocumentTypes.BANK_ACCOUNT_STATEMENT_2
      ) 
    )
  }

  getRegistrationAction = () => {
    const { registrationAction } = this.props;

    if (registrationAction) {
      return registrationAction;
    }

    const loan = this.findCurrentLoanAwaitingStatements();
    return loan ? loan.action : null;
  };

  uploadingAvailable = () => {
    const registrationAction = this.getRegistrationAction();
    return uploadDocumentsActions.includes(registrationAction);
  };

  getImportedAccountsWithBankNames = () => {
    const { psd2ImportedAccounts, psd2Institutions } = this.props;
    if (!psd2ImportedAccounts || !psd2ImportedAccounts.length) return [];
    return psd2ImportedAccounts.map(acc => {
      const inst = (psd2Institutions || []).find(i => i.id === acc.institution_id);
      return { iban: acc.iban, bankName: inst ? inst.name : acc.institution_id };
    });
  };

  // ===== PSD2 helpers =====

  // Takes array of PSD2Institution (id, name, logo, ...)
  splitInstitutions = institutions => {
    // Put SK top banks into tiles, everything else into select
    const mains = [];
    const mainIds = [
      'SLOVENSKA_GIBASKBX',
      'VUB_BANKA_SUBASKBX',
      'TATRABANKA_TATRSKBXXXX',
      'CSOB_CEKOSKBXXXX',
      'RAIFFEISEN_CCRALULLXXX',
    ];

    (mainIds || []).forEach(id => {
      const found = institutions.filter((it) => it.id === id);
      const it = found && found.length > 0 ? found[0] : null;
      const obj = {
        code: id,
        name: (it && it.name) || null,
        logo: (it && it.logo) || null, // your model uses `logo`
      };
      mains.push(obj);
    });
    const others = institutions
      .filter((it) => !mains.some((i) => i.id === it.id))
      .map((it)=>({code: it.id, name:it.name, logo:it.logo}))
    return { mains, others };
  };

  onStartPsd2 = bankCode => {
    const { actions } = this.props;
    const loan = this.findCurrentLoanAwaitingStatements();
    if (!loan) {
      // If no loan context, you can route to manual or show a message
      console.error('nemam loan')
      message.error(`Neexistuje pôžička v stave "Čaká na nahratie bankových výpisov"`, 5);
      return;
    }
    // After partner completes, redirect back with ?psd2=success or ?psd2=error
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
    const redirectUrl = `${origin}/admin/#/dashboard/documents?psd2=success`;
    actions.createPsd2Requisition({ institutionId: bankCode, loanId: loan.id, redirectUrl });
  };

  render() {
    const {
      registrationAction,
      contracts,
      token,
      isUpdating,
      isUploading,
      accountRole,
      useBiometrics,
      isSavingVerificationResult,
      usePSD2Integration,
      PSD2Discount,
      psd2Institutions,
      psd2LastResult,
    } = this.props;
    const { documents, optionalDocumentType, documentsFetched } = this.state;

    const generatedContracts = contracts.filter(
      contract =>
        (
          contract.type === contractType.GENERAL ||
          contract.type === contractType.AUTOINVEST ||
          contract.type === contractType.INFORMATION_OBLIGATIONS_DEBTOR ||
          contract.type === contractType.INFORMATION_OBLIGATIONS_INVESTOR
        ) &&
        contract.status !== contractStatus.INACTIVE
    );

    let showBiometrics = parseInt(useBiometrics) === 1 && accountRole == accountRoles.DEBTOR;

    let filteredDocuments = documents.filter(doc => doc.accountRoles.indexOf(accountRole) !== -1);
    let requiredDocumentsPaired = false;

    
    const loanAwaitingBank = this.findCurrentLoanAwaitingStatements()

    const newestLoan = this.findCurrentLoan()

    const latestLoanRequisitions = (!!loanAwaitingBank && !!loanAwaitingBank.psd2Requisitions && !!loanAwaitingBank.psd2Requisitions.length )
      ? loanAwaitingBank.psd2Requisitions
      : !!newestLoan && newestLoan.status === 'NPI' && newestLoan.psd2Requisitions.length ? newestLoan.psd2Requisitions : []

    const filteredRequisitions = latestLoanRequisitions.filter(
      (req) => req.system_status !== 'deleted' && 
        (req.status === 'LN' || 
          (req.status === 'CR' && psd2LastResult === 'success')))

    const hasPsd2Requisitions = !!filteredRequisitions.length

    const loan = this.findCurrentLoanAwaitingStatements();
    if (!registrationAction && loan) {
      filteredDocuments = filteredDocuments.filter(
        doc => doc.registrationAction === debtorRegistrationActions.UPLOAD_BANK_ACCOUNT_STATEMENTS
      );

      requiredDocumentsPaired = filteredDocuments
        .filter(doc => doc.documentObligation === accountDocumentObligations.REQUIRED)
        .every(doc => this.isDocFilled(doc,usePSD2Integration,hasPsd2Requisitions,psd2LastResult));

    } else {
      if (uploadDocumentsActions.includes(registrationAction)) {
        filteredDocuments = filteredDocuments.filter(
          doc => doc.registrationAction === registrationAction
        );

        requiredDocumentsPaired = filteredDocuments
          .filter(doc => doc.documentObligation === accountDocumentObligations.REQUIRED)
          .every(doc => this.isDocFilled(doc,usePSD2Integration,hasPsd2Requisitions,psd2LastResult));
      }
      if (
        registrationAction === debtorRegistrationActions.WAIT_FOR_DOCUMENT_CONFIRMATION ||
        !registrationAction ||
        registrationAction === debtorRegistrationActions.DOCUMENTS_REJECTED
      ) {
        filteredDocuments = filteredDocuments.filter(
          doc => doc.registrationAction === debtorRegistrationActions.UPLOAD_DOCUMENTS
        );
        requiredDocumentsPaired = true;
      }

      if (registrationAction === debtorRegistrationActions.PRESCORING_FAILED) {
        filteredDocuments = filteredDocuments.filter(doc => doc.file.id);
        requiredDocumentsPaired = true;
      }
    }

    const { mains: banksMain, others: banksOther } = this.splitInstitutions(psd2Institutions || []);

    const showAdditionalStatementsLink = parseInt(usePSD2Integration) === 1 

    return (
      <div>
        <DocumentManagement
          allowedMimeTypes={accountDocumentAllowedMimeTypes}
          documents={filteredDocuments}
          generalContract={generatedContracts}
          accountRole={accountRole}
          isSubmitDisabled={!requiredDocumentsPaired}
          isUpdating={isUpdating}
          isUploading={isUploading}
          onOptionalDocumentChange={this.onOptionalDocumentChange}
          onRemove={this.onRemove}
          onSubmit={this.onSubmit}
          onUpload={this.onUpload}
          optionalDocumentType={optionalDocumentType}
          registrationAction={this.getRegistrationAction()}
          token={token}
          saveVerificationResult={this.onSaveVerificationResult}
          documentsFetched={documentsFetched}
          useBiometrics={showBiometrics}
          isSavingVerificationResult={isSavingVerificationResult}
          // PSD2 props
          psd2Enabled={parseInt(usePSD2Integration) === 1}
          psd2LastResult={psd2LastResult}
          psd2BanksMain={banksMain}
          psd2BanksOther={banksOther}
          onStartPsd2={this.onStartPsd2}
          bankStatementsLocked={!!hasPsd2Requisitions || psd2LastResult === 'success'}
          bankStatementTypes={[
            accountDocumentTypes.BANK_ACCOUNT_STATEMENT_1,
            accountDocumentTypes.BANK_ACCOUNT_STATEMENT_2,
          ]}
          showAdditionalStatementsLink={showAdditionalStatementsLink}
          shouldShowPsd2Step={!!loanAwaitingBank || !!hasPsd2Requisitions}
          shouldPreProceedPsd2={!!hasPsd2Requisitions && psd2LastResult !=='success'}
          psd2Discount={PSD2Discount}
          psd2ImportedAccounts={this.getImportedAccountsWithBankNames()}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/documents/containers/DocumentsContainer.js